import { useParams, useNavigate } from 'react-router';
import { useState } from 'react';
import { ArrowLeft, Pencil, Check, X } from 'lucide-react';
import { useDialog } from '@hooks/useDialog';
import { useTaskData } from '../hooks/useTaskData';
import type { TaskPayload } from '../hooks/useTaskData';
import { taskRequestApi } from '../services/taskRequestApi';
import TaskStatusBadge from '../components/TaskStatusBadge';
import TaskPriorityBadge from '../components/TaskPriorityBadge';
import TaskComments from '../components/TaskComments';
import AssignUserDialog from '../components/AssignUserDialog';
import CompleteTaskDialog from '../components/CompleteTaskDialog';
import RejectTaskDialog from '../components/RejectTaskDialog';
import TaskChecklist from '../components/TaskChecklist';
import AddClinicModal from '@features/coverage/components/AddClinicModal';
import type { AddClinicInitialData } from '@features/coverage/components/AddClinicModal';
import RequirePermission from '@shared/components/RequirePermission';
import './TasksPage.css';

const TYPE_LABELS: Record<string, string> = {
  SetupNewClinic: 'Add Clinic',
  ImAddClinic: 'IM - Add Clinic',
  CreateClinicRelationship: 'Relationship',
};

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const taskId = Number(id);

  const assignDialog = useDialog();
  const completeDialog = useDialog();
  const rejectDialog = useDialog();
  const addClinicDialog = useDialog();
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');
  const [dueDateSaving, setDueDateSaving] = useState(false);

  const {
    task,
    isLoading,
    error,
    payload,
    refreshTask,
    changeStatus,
    statusLoading,
  } = useTaskData(taskId);

  const handleDialogDone = () => {
    assignDialog.close();
    completeDialog.close();
    rejectDialog.close();
    refreshTask();
  };

  const handleAddClinicDone = (clinicId: number) => {
    addClinicDialog.close();
    navigate(`/clinics/${clinicId}`);
  };

  const startEditDueDate = () => {
    const current = task?.dueDate ? task.dueDate.split('T')[0] : '';
    setDueDateValue(current);
    setEditingDueDate(true);
  };

  const saveDueDate = async () => {
    if (dueDateSaving) return;
    setDueDateSaving(true);
    try {
      await taskRequestApi.updateTaskRequest(taskId, { dueDate: dueDateValue || null });
      refreshTask();
      setEditingDueDate(false);
    } catch (err) {
      console.error('Failed to update due date:', err);
    } finally {
      setDueDateSaving(false);
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAddress = (addr: TaskPayload['address']) => {
    if (!addr) return null;
    const line1 = [addr.street, addr.suite].filter(Boolean).join(', ');
    const line2 = [addr.city, addr.state].filter(Boolean).join(', ');
    const cityStateZip = [line2, addr.zip].filter(Boolean).join(' ');
    return [line1, cityStateZip].filter(Boolean).join('\n');
  };

  const getAddClinicInitialData = (): AddClinicInitialData | undefined => {
    if (!payload?.address) return undefined;
    return {
      street: payload.address.street,
      suite: payload.address.suite,
      city: payload.address.city,
      state: payload.address.state,
      zip: payload.address.zip,
    };
  };

  if (isLoading) {
    return (
      <div className="task-detail-page">
        <div className="task-detail-loading">
          <div className="loading-spinner" />
          <p>Loading task details...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="task-detail-page">
        <div className="task-detail-page-back">
          <button type="button" className="task-action-btn task-action-btn-secondary" onClick={() => navigate('/tasks')}>
            <ArrowLeft size={16} className="task-btn-icon" />
            Back to Tasks
          </button>
        </div>
        <div className="task-detail-error">
          <p>Failed to load task details.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="task-detail-page">
        {/* Back button */}
        <div className="task-detail-page-back">
          <button type="button" className="task-action-btn task-action-btn-secondary" onClick={() => navigate('/tasks')}>
            <ArrowLeft size={16} className="task-btn-icon" />
            Back to Tasks
          </button>
        </div>

        <div className="task-detail-panel">
          {/* Header */}
          <div className="task-detail-header">
            <div className="task-detail-header-left">
              <h2>{task.title}</h2>
              <div className="task-detail-badges">
                <span className="task-type-badge">{TYPE_LABELS[task.taskType] ?? task.taskTypeDisplay}</span>
                <TaskStatusBadge status={task.status} display={task.statusDisplay} />
                <TaskPriorityBadge priority={task.priority} display={task.priorityDisplay} />
                <span className="task-detail-id">#{task.id}</span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="task-detail-body">
            {/* Context */}
            {(task.customerId || task.customerLocationId || task.clinicId || payload?.address) && (
              <div className="task-detail-section">
                <h3>Context</h3>
                <div className="task-detail-grid">
                  {(task.customerId || payload?.customerName) && (
                    <div className="task-detail-field">
                      <span className="task-detail-field-label">Customer</span>
                      <span className="task-detail-field-value">
                        {payload?.customerName ?? `#${task.customerId}`}
                      </span>
                    </div>
                  )}
                  {(task.customerLocationId || payload?.locationName) && (
                    <div className="task-detail-field">
                      <span className="task-detail-field-label">Location</span>
                      <span className="task-detail-field-value">
                        {payload?.locationName ?? `#${task.customerLocationId}`}
                      </span>
                    </div>
                  )}
                  {task.clinicId && (
                    <div className="task-detail-field">
                      <span className="task-detail-field-label">Clinic ID</span>
                      <span className="task-detail-field-value">{task.clinicId}</span>
                    </div>
                  )}
                  {payload?.address && formatAddress(payload.address) && (
                    <div className="task-detail-field task-detail-field-span">
                      <span className="task-detail-field-label">Address</span>
                      <span className="task-detail-field-value task-detail-field-preline">
                        {formatAddress(payload.address)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {task.description && (
              <div className="task-detail-section">
                <h3>Description</h3>
                <div className="task-detail-description">{task.description}</div>
              </div>
            )}

            {/* Checklist */}
            {payload?.checklist && payload.checklist.length > 0 && (
              <div className="task-detail-section">
                <TaskChecklist
                  taskId={taskId}
                  checklist={payload.checklist}
                  taskStatus={task.status}
                  taskType={task.taskType}
                  assignedToUserName={task.assignedToUserName}
                  onUpdated={refreshTask}
                  onAssignClick={assignDialog.open}
                  onSourceAction={() => {
                    if (task.taskType === 'ImAddClinic' && task.clinicId) {
                      // IM simple-add already created the clinic — open it to complete + verify.
                      navigate(`/clinics/${task.clinicId}`);
                    } else if (task.taskType === 'SetupNewClinic' || task.taskType === 'ImAddClinic') {
                      addClinicDialog.open();
                    } else {
                      const url = task.customerLocationId
                        ? '/client-relationships?locationId=' + task.customerLocationId
                        : '/client-relationships';
                      navigate(url);
                    }
                  }}
                />
              </div>
            )}

            {/* Details */}
            <div className="task-detail-section">
              <h3>Details</h3>
              <div className="task-detail-grid">
                <div className="task-detail-field">
                  <span className="task-detail-field-label">Source</span>
                  <span className="task-detail-field-value">{task.source}</span>
                </div>
                <div className="task-detail-field">
                  <span className="task-detail-field-label">Requested By</span>
                  <span className="task-detail-field-value">{task.requestedBy}</span>
                </div>
                <div className="task-detail-field">
                  <span className="task-detail-field-label">Assigned To</span>
                  <span className="task-detail-field-value">
                    {task.assignedToUserName ?? '\u2014'}
                  </span>
                </div>
                <div className="task-detail-field">
                  <span className="task-detail-field-label">Due Date</span>
                  {editingDueDate ? (
                    <div className="task-detail-inline-edit">
                      <input
                        type="date"
                        aria-label="Due date"
                        value={dueDateValue}
                        onChange={(e) => setDueDateValue(e.target.value)}
                        disabled={dueDateSaving}
                      />
                      <button
                        type="button"
                        className="task-detail-inline-save"
                        onClick={saveDueDate}
                        disabled={dueDateSaving}
                        title="Save"
                        aria-label="Save due date"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        className="task-detail-inline-cancel"
                        onClick={() => setEditingDueDate(false)}
                        disabled={dueDateSaving}
                        title="Cancel"
                        aria-label="Cancel due date edit"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <span className="task-detail-field-value task-detail-field-editable">
                      {formatDate(task.dueDate)}
                      {task.status !== 'Completed' && task.status !== 'Rejected' && (
                        <button
                          type="button"
                          className="task-detail-edit-btn"
                          onClick={startEditDueDate}
                          title="Edit due date"
                          aria-label="Edit due date"
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                    </span>
                  )}
                </div>
                <div className="task-detail-field">
                  <span className="task-detail-field-label">Created</span>
                  <span className="task-detail-field-value">{formatDateTime(task.createdAt)}</span>
                </div>
                <div className="task-detail-field">
                  <span className="task-detail-field-label">Last Modified</span>
                  <span className="task-detail-field-value">{formatDateTime(task.modifiedAt)}</span>
                </div>
              </div>
            </div>

            {/* Result (completed) */}
            {task.status === 'Completed' && (
              <div className="task-detail-section">
                <h3>Result</h3>
                <div className="task-detail-grid">
                  <div className="task-detail-field">
                    <span className="task-detail-field-label">Completed By</span>
                    <span className="task-detail-field-value">
                      {task.completedByUserName ?? '\u2014'}
                    </span>
                  </div>
                  <div className="task-detail-field">
                    <span className="task-detail-field-label">Completed At</span>
                    <span className="task-detail-field-value">
                      {formatDateTime(task.completedAt)}
                    </span>
                  </div>
                  {task.resultRecordType && (
                    <div className="task-detail-field">
                      <span className="task-detail-field-label">Result Type</span>
                      <span className="task-detail-field-value">{task.resultRecordType}</span>
                    </div>
                  )}
                  {task.resultRecordId && (
                    <div className="task-detail-field">
                      <span className="task-detail-field-label">Result Record ID</span>
                      <span className="task-detail-field-value">{task.resultRecordId}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rejection reason */}
            {task.status === 'Rejected' && task.rejectionReason && (
              <div className="task-detail-section">
                <h3>Rejection Reason</h3>
                <div className="task-detail-description">{task.rejectionReason}</div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="task-detail-actions">
            {task.status === 'New' && (
              <>
                <RequirePermission permission="UserCanEditClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-primary"
                    onClick={() => changeStatus('InProgress')}
                    disabled={statusLoading}
                  >
                    Start Working
                  </button>
                </RequirePermission>
                <RequirePermission permission="UserCanAssignClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-secondary"
                    onClick={assignDialog.open}
                    disabled={statusLoading}
                  >
                    Assign
                  </button>
                </RequirePermission>
                <RequirePermission permission="UserCanRejectClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-danger"
                    onClick={rejectDialog.open}
                    disabled={statusLoading}
                  >
                    Reject
                  </button>
                </RequirePermission>
              </>
            )}

            {task.status === 'InProgress' && (
              <>
                <RequirePermission permission="UserCanCompleteClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-success"
                    onClick={completeDialog.open}
                    disabled={statusLoading}
                  >
                    Complete
                  </button>
                </RequirePermission>
                <RequirePermission permission="UserCanAssignClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-secondary"
                    onClick={assignDialog.open}
                    disabled={statusLoading}
                  >
                    Assign
                  </button>
                </RequirePermission>
                <RequirePermission permission="UserCanRejectClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-danger"
                    onClick={rejectDialog.open}
                    disabled={statusLoading}
                  >
                    Reject
                  </button>
                </RequirePermission>
                <RequirePermission permission="UserCanEditClinicTaskRequest">
                  <button
                    type="button"
                    className="task-action-btn task-action-btn-secondary"
                    onClick={() => changeStatus('New')}
                    disabled={statusLoading}
                  >
                    Revert to New
                  </button>
                </RequirePermission>
              </>
            )}

            {task.status === 'Rejected' && (
              <RequirePermission permission="UserCanEditClinicTaskRequest">
                <button
                  type="button"
                  className="task-action-btn task-action-btn-primary"
                  onClick={() => changeStatus('New')}
                  disabled={statusLoading}
                >
                  Reopen
                </button>
              </RequirePermission>
            )}
          </div>

          {/* Comments */}
          <TaskComments taskId={taskId} />
        </div>
      </div>

      {/* Dialogs */}
      {assignDialog.isOpen && (
        <AssignUserDialog
          taskId={taskId}
          currentAssignee={task.assignedToUserName}
          onAssigned={handleDialogDone}
          onCancel={assignDialog.close}
        />
      )}

      {completeDialog.isOpen && (
        <CompleteTaskDialog
          taskId={taskId}
          onCompleted={handleDialogDone}
          onCancel={completeDialog.close}
        />
      )}

      {rejectDialog.isOpen && (
        <RejectTaskDialog
          taskId={taskId}
          onRejected={handleDialogDone}
          onCancel={rejectDialog.close}
        />
      )}

      {addClinicDialog.isOpen && (
        <AddClinicModal
          parentClinicId={null}
          onClose={addClinicDialog.close}
          onSaved={handleAddClinicDone}
          initialData={getAddClinicInitialData()}
        />
      )}
    </>
  );
}
